import { LitElement, html, css } from 'lit';
import { sharedStyles } from './base.js';

// <gt-card> — the standard panel surface. Header (heading + optional chip),
// a default-slot body, and an optional footer deep-link.
//
//   <gt-card heading="Market Mood" href="/macro/">
//     <gt-chip slot="chip" tone="live">Live</gt-chip>
//     ...body...
//   </gt-card>
//
// Charts/canvas can be slotted into the body untouched — the card never
// re-renders slotted light-DOM children, so ECharts/TradingView mounts survive.
export class GtCard extends LitElement {
  static properties = {
    heading: { type: String },
    href: { type: String },
    footer: { type: String },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .card {
        display: flex;
        flex-direction: column;
        background: var(--bg-card, #141826);
        border: 1px solid var(--border, #1e2535);
        border-radius: var(--radius-lg, 10px);
        overflow: hidden;
        height: 100%;
      }
      header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2, 8px);
        padding: var(--space-4, 16px) var(--space-4, 16px) var(--space-3, 12px);
      }
      .heading {
        font-size: var(--fs-sm, 0.82rem);
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--text-strong, #e8dfc0);
        margin: 0;
      }
      .body {
        padding: 0 var(--space-4, 16px) var(--space-4, 16px);
        flex: 1;
      }
      :host([bare]) .card {
        background: none;
        border: 0;
        border-radius: 0;
      }
      footer {
        border-top: 1px solid var(--border-soft, #141826);
        padding: var(--space-3, 12px) var(--space-4, 16px);
      }
      footer a {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: var(--fs-xs, 0.72rem);
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--accent, #c8a84b);
        text-decoration: none;
      }
      footer a:hover {
        filter: brightness(1.15);
      }
      footer a::after {
        content: '→';
        transition: transform 0.12s;
      }
      footer a:hover::after {
        transform: translateX(3px);
      }
    `,
  ];

  render() {
    const showHeader = this.heading != null;
    return html`
      <div class="card">
        ${showHeader
          ? html`<header>
              <h3 class="heading">${this.heading}</h3>
              <slot name="chip"></slot>
            </header>`
          : null}
        <div class="body"><slot></slot></div>
        ${this.href
          ? html`<footer>
              <a href=${this.href}>${this.footer || 'View'}</a>
            </footer>`
          : null}
      </div>
    `;
  }
}

customElements.define('gt-card', GtCard);
